import {useIntl} from 'react-intl';
import {Alert} from '@material-ui/lab';
import {AppButton} from '@components/Core/buttons/AppButton/AppButton';
import {AppOutlinedButton} from '@components/Core/buttons/AppOutlinedButton/AppOutlinedButton';
import type {HardwareTokenError as HardwareTokenErrorType} from '../types';
import {
    ERROR_CODE_TO_MESSAGE_KEY,
    DEFAULT_ERROR_MESSAGE_KEY,
    TOKEN_READER_CONNECTION_FAILED_MESSAGE,
    TOKEN_READER_DOWNLOAD_URL
} from '../constants';
import './HardwareTokenError.less';

interface HardwareTokenErrorProps {
    error: HardwareTokenErrorType;
    onRetry: () => void;
    onClose: () => void;
}

function getErrorMessageKey(errorCode: string): string {
    return ERROR_CODE_TO_MESSAGE_KEY[errorCode] || DEFAULT_ERROR_MESSAGE_KEY;
}

function getIsTokenReaderNotFound(error: HardwareTokenErrorType): boolean {
    return error.message === TOKEN_READER_CONNECTION_FAILED_MESSAGE;
}

/**
 * Error display component for hardware token signing failures.
 * Uses MUI Alert for consistent error presentation.
 */
export function HardwareTokenError({
    error,
    onRetry,
    onClose
}: HardwareTokenErrorProps): JSX.Element {
    const {formatMessage} = useIntl();

    const messageKey = getErrorMessageKey(error.code);
    const isTokenReaderNotFound = getIsTokenReaderNotFound(error);

    return (
        <div
            className="hardware-token-error"
            data-wdio="test-hardware-token-error"
        >
            <Alert severity="error" variant="outlined" className="hardware-token-error-alert">
                {formatMessage({id: messageKey, defaultMessage: error.message})}
            </Alert>
            {isTokenReaderNotFound && (
                <a
                    href={TOKEN_READER_DOWNLOAD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hardware-token-error-download"
                    data-wdio="test-hardware-token-error-download"
                >
                    {formatMessage({
                        id: 'hardwareToken.error.downloadTokenReader',
                        defaultMessage: 'Download the latest version of the Token Reader'
                    })}
                </a>
            )}
            <div className="hardware-token-error-actions">
                <AppOutlinedButton
                    onClick={onClose}
                    data-wdio="test-hardware-token-error-close"
                >
                    {formatMessage({id: 'hardwareToken.button.close', defaultMessage: 'Close'})}
                </AppOutlinedButton>
                <AppButton
                    onClick={onRetry}
                    data-wdio="test-hardware-token-error-retry"
                >
                    {formatMessage({id: 'hardwareToken.button.retry', defaultMessage: 'Retry'})}
                </AppButton>
            </div>
        </div>
    );
}
